import { IValueInputAttachable } from "client/Forms/attachable";
import {
	ReactElement,
	RefObject,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState } from "react";
import {
	HubItemCategoryNodeFormModel,
	HubItemChildNodeFormModel,
	HubItemGroupNodeFormModel,
	HubItemRootNodeFormModel
} from "./common";
import {
	HubCategorySavedResult,
	useEditHubCategoryDialog
} from "../Management/EditHubCategoryDialog/EditHubCategoryDialog";
import {
	getClientId,
	setClientId
} from "client/Misc/clientId";
import {
	ActionListItem,
	DropOverActionList,
	FormattedText,
	IconButton,
	ReorderableList,
	ReorderableListAddButtonClickEventArgs,
	Typography,
	useConfirmationDialog
} from "client/Components";
import {
	ensureNotNil,
	generateTrackingId,
	isNotNil
} from "client/Misc/utils";
import { HubSidebarCategoryItem } from "./HubSidebarCategoryItem";
import { HubSidebarGroupItem } from "./HubSidebarGroupItem";
import { HubSidebarItemContent } from "./HubSidebarItemContent";
import { hubItemKindToIcon } from "../HubThumbnail/HubThumbnail";
import { localize } from "client_legacy/ndaw/culture";
import { Coordinate } from "client/Misc/commonTypes";
import { useDeleteHubItemCategoriesDialog } from "../Management/DeleteHubItemCategoriesDialog";
import { Guid } from "client_legacy/ndaw/guid";
import { HubItemCategoryNodeKind } from "client/Api/Hub/common";
import { HubDataSource } from "client/Api/Hub/hub";
import { HubItemKind } from "client/Api/Hub/hubItemCategories";
import { hubDataSourceToHubItemKindMap } from "../Sidebar/common";
import { EditHubItemGroupSaveResult, useEditHubItemGroupDialog } from "./EditHubItemGroupDialog";
import {
	OpenItemMenuState,
	useItemDropOverMenuProps,
	useItemMenuState
} from "client/Hooks";

export type HubItemNodeProps = {
	attachable: IValueInputAttachable<HubItemRootNodeFormModel>;
	canReorderItems: boolean;
	nodeClientIdToScrollTo: Guid | null;
	onNodeClientIdToScrollToChange: (id: Guid | null) => void;
	doesMatchSearchTerm: (item: HubItemChildNodeFormModel) => boolean;
	registerDialogWithBackdrop: () => (() => void);
	onNodeChanged: () => void;
	onNewNodeAdded: (node: HubItemChildNodeFormModel) => void;
	onItemReorder: () => void;
};

export const HubItemNode = (props: HubItemNodeProps): ReactElement => {
	const {
		attachable,
		canReorderItems,
		nodeClientIdToScrollTo,
		onNodeClientIdToScrollToChange,
		doesMatchSearchTerm,
		registerDialogWithBackdrop,
		onNodeChanged,
		onNewNodeAdded,
		onItemReorder
	} = props;

	const [addNewMenuState, setAddNewMenuState] = useState<ActionsMenuState | null>(null);

	const addButtonRef: RefObject<HTMLButtonElement | null> = useRef(null);
	const menuButtonRef: RefObject<HTMLButtonElement | null> = useRef<HTMLButtonElement>(null);

	const [editHubCategoryDialog, openEditHubCategoryDialog] = useEditHubCategoryDialog();
	const [editGroupDialog, openEditGroupDialog] = useEditHubItemGroupDialog();
	const [confirmationDialog, openConfirmationDialog] = useConfirmationDialog();
	const [deleteHubItemCategoriesDialog, openDeleteHubItemCategoriesDialog] = useDeleteHubItemCategoriesDialog();

	const {
		menuState,
		onOpenMoreMenu,
		onOpenContextMenu,
		onCloseMenu
	} = useItemMenuState();

	const hasDialogWithBackdrop: boolean = isNotNil(editHubCategoryDialog) || isNotNil(editGroupDialog);

	const groupsHierarchy = useMemo(
		(): readonly HubItemGroupNodeFormModel[] => getGroups(
			attachable
				.value
				.children
		),
		[attachable.value]
	);

	useEffect(
		(): (() => void) | void => {
			if (!hasDialogWithBackdrop) {
				return;
			}

			return registerDialogWithBackdrop();
		},
		[hasDialogWithBackdrop, registerDialogWithBackdrop]
	);

	const {
		source,
		children
	} = attachable.value;

	const hubItemKind: HubItemKind = hubDataSourceToHubItemKindMap[source];

	const typeItemClientId: Guid = getClientId(attachable.value);

	const itemMoveHandler = useCallback(
		(fromIndex: number, toIndex: number): void => {
			onItemReorder();

			const itemsCopy: HubItemChildNodeFormModel[] = [...children];
			const item: HubItemChildNodeFormModel = itemsCopy.splice(fromIndex, 1)[0];
			itemsCopy.splice(toIndex, 0, item);

			attachable.for("children")
				.reducer
				.setValue(itemsCopy);
		},
		[
			attachable,
			children,
			onItemReorder
		]
	);

	const openMenuHandler = useCallback(
		(args: ReorderableListAddButtonClickEventArgs): void => {
			setAddNewMenuState({
				coordinate: {
					x: args.clientX,
					y: args.clientY
				}
			});
		},
		[]
	);

	const createGroupHandler = useCallback(
		async (parentClientId: Guid): Promise<void> => {
			let childrenHandler: (IValueInputAttachable<readonly HubItemChildNodeFormModel[]>) | null = null;

			if (parentClientId === typeItemClientId) {
				childrenHandler = attachable.for("children");
			} else {
				const result: TryResult<IValueInputAttachable<HubItemGroupNodeFormModel>> = tryFindGroupItem(
					parentClientId,
					attachable
				);

				if (result.wasSuccessful) {
					childrenHandler = result.result
						.for("groupData")
						.for("children");
				}
			}

			if (!childrenHandler) {
				console.error(`Could not find item with client Id ${parentClientId}`);
				return;
			}

			const creationResult: TryResult<EditHubItemGroupSaveResult> = await openEditGroupDialog({
				mode: {
					kind: "new",
					itemKind: hubItemKind
				}
			});

			if (!creationResult.wasSuccessful) {
				return;
			}

			const { result: saveResult } = creationResult;

			const newGroup: HubItemGroupNodeFormModel = setClientId<Omit<HubItemGroupNodeFormModel, "__hasClientId">>({
				kind: HubItemCategoryNodeKind.Group,
				name: saveResult.name,
				groupData: {
					groupId: saveResult.id,
					children: []
				}
			});

			childrenHandler
				.reducer
				.setValue([
					...childrenHandler.value,
					newGroup
				]);

			childrenHandler
				.validation
				.triggerInputFinished();

			onNewNodeAdded(newGroup);
			onNodeChanged();
		},
		[
			attachable,
			hubItemKind,
			typeItemClientId,
			onNewNodeAdded,
			openEditGroupDialog,
			onNodeChanged
		]
	);

	const editGroupHandler = useCallback(
		async (clientId: Guid): Promise<void> => {
			const findParentAndIndexResult: TryResult<ParentAndItemIndex> = tryFindParentChildrenAttachableAndItemIndex(
				clientId,
				attachable
			);

			if (!findParentAndIndexResult.wasSuccessful) {
				console.error(`Can not find the item with client ${clientId}`);
				return;
			}

			const {
				parentChildrenAttachable,
				itemIndex
			} = findParentAndIndexResult.result;

			const itemAttachable: IValueInputAttachable<HubItemGroupNodeFormModel> = parentChildrenAttachable
				.forIndex(itemIndex)
				.assertType((x) => x.kind === HubItemCategoryNodeKind.Group);

			const item: HubItemGroupNodeFormModel = itemAttachable.value;

			const updateResult: TryResult<EditHubItemGroupSaveResult> = await openEditGroupDialog({
				mode: {
					kind: "update",
					id: item.groupData
						.groupId
				}
			});

			if (!updateResult.wasSuccessful) {
				return;
			}

			const {
				name
			} = updateResult.result;

			const nameAttachable: IValueInputAttachable<string> = itemAttachable.for("name");

			nameAttachable.reducer
				.setValue(name);

			nameAttachable.validation
				.triggerInputFinished();

			onNodeChanged();
		},
		[
			attachable,
			openEditGroupDialog,
			onNodeChanged
		]
	);

	const deleteGroupHandler = useCallback(
		async(clientId: Guid): Promise<void> => {
			const findParentAndIndexResult: TryResult<ParentAndItemIndex> = tryFindParentChildrenAttachableAndItemIndex(
				clientId,
				attachable
			);

			if (!findParentAndIndexResult.wasSuccessful) {
				console.error(`Can not find the item with client ${clientId}`);
				return;
			}

			const {
				parentChildrenAttachable,
				itemIndex
			} = findParentAndIndexResult.result;

			const item: HubItemChildNodeFormModel = parentChildrenAttachable
				.forIndex(itemIndex)
				.assertType((x) => x.kind === HubItemCategoryNodeKind.Group)
				.value;

			const result: TryResult<void> = await openConfirmationDialog({
				title: (
					<FormattedText
						text={localize("_AreYouSureYouWantToDeleteName")}
						thingsToReplace={[
							{
								what: "{Name}",
								filler: item.name
							}
						]}
					/>
				),
				confirmText: localize("_Delete"),
				children: (
					<FormattedText
						text={localize("_NoteTheGroupsAndCategoriesWithinTheGroupWillNotBeDeleted")}
						thingsToWrap={[
							{
								blockId: "Note",
								wrap: x => <b>{x}</b>
							}
						]}
					/>
				),
				trackingId: "delete-hub-item-group-confirmation-dialog"
			});

			if (!result.wasSuccessful) {
				return;
			}

			parentChildrenAttachable
				.reducer
				.setValue([
					...parentChildrenAttachable.value,
					...item.groupData
						.children
				]);

			parentChildrenAttachable
				.reducer
				.removeAt(itemIndex);

			parentChildrenAttachable
				.validation
				.triggerInputFinished();

			onNodeChanged();
		},
		[
			attachable,
			openConfirmationDialog,
			onNodeChanged
		]
	);

	const createCategoryHandler = useCallback(
		async(parentClientId: Guid): Promise<void> => {
			const result: TryResult<HubCategorySavedResult> = await openEditHubCategoryDialog({
				kind: hubItemKind,
				id: null
			});

			if (!result.wasSuccessful) {
				return;
			}

			onNodeChanged();

			let childrenHandler: (IValueInputAttachable<readonly HubItemChildNodeFormModel[]>) | null = null;

			if (parentClientId === typeItemClientId) {
				childrenHandler = attachable.for("children");
			} else {
				const result: TryResult<IValueInputAttachable<HubItemGroupNodeFormModel>> = tryFindGroupItem(
					parentClientId,
					attachable
				);

				if (result.wasSuccessful) {
					childrenHandler = result.result
						.for("groupData")
						.for("children");
				}
			}

			if (!childrenHandler) {
				console.error(`Could not find item with client Id ${parentClientId}`);
				return;
			}

			const {
				id,
				name
			} = result.result;

			const newCategory: HubItemCategoryNodeFormModel = setClientId({
				kind: HubItemCategoryNodeKind.Category,
				name,
				categoryData: {
					categoryId: id,
				}
			});

			childrenHandler
				.reducer
				.push(newCategory);

			childrenHandler
				.validation
				.triggerInputFinished();

			onNewNodeAdded(newCategory);
		},
		[
			attachable,
			typeItemClientId,
			hubItemKind,
			openEditHubCategoryDialog,
			onNodeChanged,
			onNewNodeAdded
		]
	);

	const editCategoryHandler = useCallback(
		async(clientId: Guid): Promise<void> => {
			const findParentAndIndexResult: TryResult<ParentAndItemIndex> = tryFindParentChildrenAttachableAndItemIndex(
				clientId,
				attachable
			);

			if (!findParentAndIndexResult.wasSuccessful) {
				console.error(`Can not find the item with client ${clientId}`);
				return;
			}

			const {
				parentChildrenAttachable,
				itemIndex
			} = findParentAndIndexResult.result;

			const item: HubItemCategoryNodeFormModel = parentChildrenAttachable
				.forIndex(itemIndex)
				.assertType((x) => x.kind === HubItemCategoryNodeKind.Category)
				.value;

			const result: TryResult<HubCategorySavedResult> = await openEditHubCategoryDialog({
				kind: hubItemKind,
				id: item.categoryData
					.categoryId
			});

			if (!result.wasSuccessful) {
				return;
			}

			onNodeChanged();

			const {
				name
			} = result.result;

			parentChildrenAttachable
				.forIndex(itemIndex)
				.reducer
				.setValue({
					...item,
					name: name
				});

			parentChildrenAttachable
				.validation
				.triggerInputFinished();
		},
		[
			hubItemKind,
			openEditHubCategoryDialog,
			attachable,
			onNodeChanged
		]
	);

	const deleteCategoryHandler = useCallback(
		async(clientId: Guid): Promise<void> => {
			const findParentAndIndexResult: TryResult<ParentAndItemIndex> = tryFindParentChildrenAttachableAndItemIndex(
				clientId,
				attachable
			);

			if (!findParentAndIndexResult.wasSuccessful) {
				console.error(`Can not find the item with client ${clientId}`);
				return;
			}

			const {
				parentChildrenAttachable,
				itemIndex
			} = findParentAndIndexResult.result;

			const item: HubItemCategoryNodeFormModel = parentChildrenAttachable
				.forIndex(itemIndex)
				.assertType((x) => x.kind === HubItemCategoryNodeKind.Category)
				.value;

			const result: TryResult<void> = await openDeleteHubItemCategoriesDialog({
				categoryIds: [
					item.categoryData
						.categoryId
				],
				hubItemKind: hubItemKind
			});

			if (!result.wasSuccessful) {
				return;
			}

			onNodeChanged();

			parentChildrenAttachable
				.reducer
				.removeAt(itemIndex);

			parentChildrenAttachable
				.validation
				.triggerInputFinished();
		},
		[
			hubItemKind,
			openDeleteHubItemCategoriesDialog,
			attachable,
			onNodeChanged
		]
	);

	const moveToTopHandler = useCallback(
		(clientId: Guid): void => {
			const result: TryResult<ParentAndItemIndex> = tryFindParentChildrenAttachableAndItemIndex(
				clientId,
				attachable
			);

			if (!result.wasSuccessful) {
				console.error(`Can not find the item with client ${clientId}`);
				return;
			}

			const {
				parentChildrenAttachable,
				itemIndex
			} = result.result;

			const item: HubItemChildNodeFormModel = parentChildrenAttachable
				.forIndex(itemIndex)
				.value;

			attachable
				.for("children")
				.reducer
				.push(item);

			attachable
				.for("children")
				.validation
				.triggerInputFinished();

			parentChildrenAttachable
				.reducer
				.removeAt(itemIndex);
		},
		[attachable]
	);

	const moveToGroupHandler = useCallback(
		(clientId: Guid, targetGroupId: Guid): void => {
			const findParentAndIndexResult: TryResult<ParentAndItemIndex> = tryFindParentChildrenAttachableAndItemIndex(
				clientId,
				attachable
			);

			if (!findParentAndIndexResult.wasSuccessful) {
				console.error(`Can not find the item with client ${clientId}`);
				return;
			}

			const {
				parentChildrenAttachable,
				itemIndex
			} = findParentAndIndexResult.result;

			const item: HubItemChildNodeFormModel = parentChildrenAttachable
				.forIndex(itemIndex)
				.value;

			const result: TryResult<IValueInputAttachable<HubItemGroupNodeFormModel>> = tryFindGroupItem(
				targetGroupId,
				attachable
			);

			if (!result.wasSuccessful) {
				console.error(`Couldn't find target group with client id ${targetGroupId}`);
				return;
			}

			const {
				result: targetGroupHandler
			} = result;

			targetGroupHandler
				.for("groupData")
				.for("children")
				.reducer
				.push(item);

			targetGroupHandler
				.for("groupData")
				.for("children")
				.validation
				.triggerInputFinished();

			parentChildrenAttachable
				.reducer
				.removeAt(itemIndex);

			parentChildrenAttachable
				.validation
				.triggerInputFinished();
		},
		[attachable]
	);

	const getOverlayTextHandler = useCallback(
		(activeIndex: number): string => {
			const activeItem: HubItemChildNodeFormModel = ensureNotNil(children[activeIndex]);
			return activeItem.name;
		},
		[children]
	);

	const renderCategoryItem = (
		handler: IValueInputAttachable<HubItemCategoryNodeFormModel>
	): ReactElement =>{
		return (
			<HubSidebarCategoryItem
				key={getClientId(handler.value)}
				attachable={handler}
				onEdit={editCategoryHandler}
				onDelete={deleteCategoryHandler}
				onMoveToGroup={moveToGroupHandler}
				groupsHierarchy={groupsHierarchy}
				nodeClientIdToScrollTo={nodeClientIdToScrollTo}
				onNodeClientIdToScrollChange={onNodeClientIdToScrollToChange}
			/>
		);
	};

	const renderGroupItem = (
		handler: IValueInputAttachable<HubItemGroupNodeFormModel>
	): ReactElement => {
		return (
			<HubSidebarGroupItem
				key={getClientId(handler.value)}
				attachable={handler}
				hubItemKind={hubItemKind}
				canReorderItems={canReorderItems}
				groupsHierarchy={groupsHierarchy}
				doesMatchSearchTerm={doesMatchSearchTerm}
				onCreateCategory={createCategoryHandler}
				onEditCategory={editCategoryHandler}
				onCreateGroup={createGroupHandler}
				onEditGroup={editGroupHandler}
				onDeleteGroup={deleteGroupHandler}
				onMoveToTop={moveToTopHandler}
				onDeleteCategory={deleteCategoryHandler}
				onMoveToGroup={moveToGroupHandler}
				nodeClientIdToScrollTo={nodeClientIdToScrollTo}
				onNodeClientIdToScrollChange={onNodeClientIdToScrollToChange}
				isInTopLevel
				onItemReorder={onItemReorder}
			/>
		);
	};

	return (
		<>
			<ReorderableList trackingId="hub-sidebar-type-item-list">
				<ReorderableList.Group
					headerContent={(
						<HubSidebarItemContent
							trackingId="content"
							icon={{
								kind: "font",
								name: hubItemKindToIcon[hubItemKind],
								color: "primary"
							}}
						>
							<Typography weight="bold">
								{localize(hubDataSourceToLocalizationKeyMap[hubItemKind])}
							</Typography>
						</HubSidebarItemContent>
					)}
					actions={(
						<IconButton
							ref={menuButtonRef}
							icon="ellipsis"
							kind="transparent"
							shape="round"
							trackingId="open-actions"
							onClick={() => onOpenMoreMenu()}
							isActive={menuState.isOpen && !menuState.isContextMenu}
						/>
					)}
					trackingId={generateTrackingId("type-item", `|${hubItemKind}`)}
					mouseInteraction={{
						onContextMenu: onOpenContextMenu
					}}
					isActive={menuState.isOpen}
				>
					<ReorderableList
						onItemMove={canReorderItems ? itemMoveHandler : undefined}
						getOverlayText={getOverlayTextHandler}
						trackingId="type-item-children-list"
					>
						{attachable.for("children")
							.map((x) => (
								x.when(
									doesMatchSearchTerm,
									itemAttachable => itemAttachable.whenType(
										(x) => x.kind === HubItemCategoryNodeKind.Category,
										renderCategoryItem,
										renderGroupItem
									)
								)
							))
						}
					</ReorderableList>

					<ReorderableList.AddButton
						ref={addButtonRef}
						onClick={openMenuHandler}
						trackingId="add-button"
					/>

					{(addNewMenuState && addButtonRef.current) && (
						<DropOverActionList
							target={addButtonRef.current}
							onClose={() => setAddNewMenuState(null)}
							contentAlignment="tl"
							targetAlignment={{
								kind: "absolute",
								coordinate: addNewMenuState.coordinate
							}}
							menuTitle={localize("_Add")}
						>
							<ActionListItem
								onClick={() => createCategoryHandler(typeItemClientId)}
								text={localize("_CreateCategory")}
								trackingId="create-category-button"
								icon="note-sticky"
							/>
							<ActionListItem
								onClick={() => createGroupHandler(typeItemClientId)}
								text={localize("_CreateGroup")}
								trackingId="create-group"
								icon="layer-group"
							/>
						</DropOverActionList>
					)}

					{editHubCategoryDialog}
					{editGroupDialog}
					{confirmationDialog}
					{deleteHubItemCategoriesDialog}
				</ReorderableList.Group>
			</ReorderableList>
			{menuState.isOpen && (
				<Menu
					target={ensureNotNil(menuButtonRef.current)}
					onClose={onCloseMenu}
					menuState={menuState}
					onCreateCategory={() => createCategoryHandler(typeItemClientId)}
					onCreateGroup={() => createGroupHandler(typeItemClientId)}
					trackingId={generateTrackingId("open-actions", "__dropover")}
				/>
			)}

		</>
	);
};
HubItemNode.displayName = "HubItemNode";

export const hubDataSourceToLocalizationKeyMap: Record<HubDataSource, LocalizationKey> = {
	[HubDataSource.Announcement]: "_Announcements",
	[HubDataSource.KnowledgeItem]: "_KnowledgeBase",
	[HubDataSource.Event]: "_Events",
	[HubDataSource.Community]: "_Communities"
};

type ActionsMenuState = {
	coordinate: Coordinate;
};

type MenuProps = {
	target: HTMLElement;
	onClose: () => void;
	menuState: OpenItemMenuState;
	trackingId: string;
	onCreateCategory(): void;
	onCreateGroup(): void;
};

const Menu = (props: MenuProps): ReactElement => {
	const {
		target,
		onClose,
		menuState,
		trackingId,
		onCreateCategory,
		onCreateGroup
	} = props;

	const {
		contentAlignment,
		targetAlignment,
		closeOnMouseDown
	} = useItemDropOverMenuProps(
		menuState,
		{
			moreMenuContentAlignment: "tr",
			moreMenuTargetAlignment: "br"
		}
	);

	return (
		<DropOverActionList
			target={target}
			onClose={onClose}
			menuTitle={localize("_Add")}
			targetAlignment={targetAlignment}
			contentAlignment={contentAlignment}
			closeOnMouseDown={closeOnMouseDown}
			trackingId={trackingId}
		>
			<ActionListItem
				onClick={onCreateCategory}
				text={localize("_CreateCategory")}
				trackingId="create-category-button"
				icon="note-sticky"
			/>
			<ActionListItem
				onClick={onCreateGroup}
				text={localize("_CreateGroup")}
				trackingId="create-group"
				icon="layer-group"
			/>
		</DropOverActionList>
	);
};
Menu.displayName = "Menu";

function tryFindGroupItem(
	clientId: Guid,
	attachable: IValueInputAttachable<HubItemRootNodeFormModel>
): TryResult<IValueInputAttachable<HubItemGroupNodeFormModel>> {
	let result: TryResult<IValueInputAttachable<HubItemGroupNodeFormModel>> = { wasSuccessful: false };

	const findParentAndIndexResult: TryResult<ParentAndItemIndex> = tryFindParentChildrenAttachableAndItemIndex(
		clientId,
		attachable
	);

	if (findParentAndIndexResult.wasSuccessful) {
		const {
			parentChildrenAttachable,
			itemIndex
		} = findParentAndIndexResult.result;

		const itemHandler: IValueInputAttachable<HubItemChildNodeFormModel> = parentChildrenAttachable
			.forIndex(itemIndex);

		itemHandler.whenType(
			(x) => x.kind === HubItemCategoryNodeKind.Group,
			x => {
				result = {
					wasSuccessful: true,
					result: x
				};
			}
		);
	}

	return result;
}

type ParentAndItemIndex = {
	parentChildrenAttachable: IValueInputAttachable<readonly HubItemChildNodeFormModel[]>;
	itemIndex: number;
};

function tryFindParentChildrenAttachableAndItemIndex(
	clientId: Guid,
	attachable: IValueInputAttachable<HubItemRootNodeFormModel>
): TryResult<ParentAndItemIndex> {
	let result: TryResult<ParentAndItemIndex> = {
		wasSuccessful: false
	};

	attachable.for("children")
		.forEach(
			(itemHandler, index) => {
				if (result.wasSuccessful) {
					return;
				}

				if (getClientId(itemHandler.value) === clientId) {
					result = {
						wasSuccessful: true,
						result: {
							parentChildrenAttachable: attachable.for("children"),
							itemIndex: index
						}
					};
				} else {
					itemHandler.whenType(
						(x) => x.kind === HubItemCategoryNodeKind.Group,
						groupHandler => {
							const tryResult = tryFindParentChildrenAttachableAndItemIndexRecursive(
								clientId,
								groupHandler
							);

							if (tryResult.wasSuccessful) {
								result = tryResult;
							}
						}
					);
				}
			}
		);

	return result;
}

function tryFindParentChildrenAttachableAndItemIndexRecursive(
	clientId: Guid,
	attachable: IValueInputAttachable<HubItemGroupNodeFormModel>
): TryResult<ParentAndItemIndex> {
	let result: TryResult<ParentAndItemIndex> = {
		wasSuccessful: false
	};

	attachable
		.for("groupData")
		.for("children")
		.forEach(
			(itemHandler, index) => {
				if (result.wasSuccessful) {
					return;
				}

				if (getClientId(itemHandler.value) === clientId) {
					result = {
						wasSuccessful: true,
						result: {
							parentChildrenAttachable: attachable.for("groupData")
								.for("children"),
							itemIndex: index
						}
					};
				} else {
					itemHandler.whenType(
						(x) => x.kind === HubItemCategoryNodeKind.Group,
						groupHandler => {
							const tryResult = tryFindParentChildrenAttachableAndItemIndexRecursive(
								clientId,
								groupHandler
							);

							if (tryResult.wasSuccessful) {
								result = tryResult;
							}
						}
					);
				}
			}
		);

	return result;
}

function getGroups(
	items: readonly HubItemChildNodeFormModel[]
): readonly HubItemGroupNodeFormModel[] {
	return items.filter(
		(x) => x.kind === HubItemCategoryNodeKind.Group
	).map(
		(x): HubItemGroupNodeFormModel => ({
			...x,
			groupData: {
				...x.groupData,
				children: getGroups(x.groupData.children)
			}
		})
	);
}